import {
  IndividualResult,
  LastStudentResponse,
  Student,
} from '../classroom.constants';
import type { MultipleChoiceAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { MultipleChoiceOption } from 'approot/activity-builder/components/multiple-choice/multiple-choice.constants';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import { sortIndividualResults } from '../logic/sort-individual-results';
import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';

type IndividualResultWithAnswer = IndividualResult & {
  answer: MultipleChoiceAnswerFragment | null;
};

export function prepareResultsPerStudent({
  studentsInClass,
  lastStudentResponses,
  options,
}: {
  studentsInClass: Student[];
  lastStudentResponses: LastStudentResponse[];
  options: MultipleChoiceOption[];
}): IndividualResultWithAnswer[] {
  const correctAnswerOptions = options.filter(o => o.correctAnswerBoolean);

  const results = studentsInClass.map(student => {
    const response = lastStudentResponses.find(
      ({ studentId }) => studentId === student.id
    );

    if (response) {
      const selectedAnswerOptions = (
        response.lastAnswer?.answer as MultipleChoiceAnswerFragment
      ).multipleChoiceItems.filter(option => option.isSelected);

      const isCorrect =
        selectedAnswerOptions.length === correctAnswerOptions.length &&
        correctAnswerOptions.every(({ itemId }) =>
          selectedAnswerOptions.some(option => option.itemId === itemId)
        );

      return {
        studentId: student.id,
        studentName: getStudentDisplayName(student),
        studentEmoji: student.emeji,
        isCorrect,
        isPending: false,
        attempts: response.numberOfAttempts,
        answer: response.lastAnswer?.answer as MultipleChoiceAnswerFragment,
      };
    }

    return {
      studentId: student.id,
      studentName: getStudentDisplayName(student),
      studentEmoji: student.emeji,
      isCorrect: false,
      isPending: true,
      attempts: 0,
      answer: null,
    };
  });

  return sortIndividualResults(results);
}

export function getMultiChoiceStudentAnswerCount(
  answers: AnswerFragment[]
): number {
  const studentIds = new Set<number>();

  answers.forEach(response => {
    if (
      response.studentId &&
      (response.answer as MultipleChoiceAnswerFragment).multipleChoiceItems
        .length > 0
    ) {
      studentIds.add(response.studentId);
    }
  });

  return studentIds.size;
}
